import {
	StyleSheet,
	Text,
	View,
	ScrollView,
	TouchableOpacity,
} from "react-native";
import React, { useState } from "react";
import InputText from "../../../components/InputText";
import { Button, useTheme } from "react-native-paper";
import { Dropdown } from "react-native-material-dropdown";
import useApi from "../../../hooks/useAPI";
import { showMessage } from "react-native-flash-message";
import LinearLoading from "../../../components/LinearLoading";
import { SafeAreaView } from "react-native-safe-area-context";
import CheckBox from "react-native-check-box";
import { useForm, Controller } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import singUpSchema from "../../../schemas/signUpSchema";
import { CreateAccountScreenProps } from "./NewUserStack";

type inputsData = {
	name: string;
	email: string;
	confirmEmail: string;
	password: string;
	confirmPassword: string;
	accountType: string;
	termsAgreed: boolean;
};

const CreateAccount = ({ navigation }: CreateAccountScreenProps) => {
	const api = useApi();
	const { colors } = useTheme();
	const [loading, setLoading] = useState(false);

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<inputsData>({
		resolver: joiResolver(singUpSchema),
	});

	const onSubmit = async (dataOfInputs: inputsData) => {
		setLoading(true);
		try {
			let accountType;
			if (dataOfInputs.accountType === "Estudante") accountType = "student";
			if (dataOfInputs.accountType === "Responsável") accountType = "guardian";

			const { data } = await api.firstAccess.signUp({
				name: dataOfInputs.name,
				email: dataOfInputs.email,
				password: dataOfInputs.password,
				account_type: accountType,
			});

			if (data === "Created") {
				showMessage({
					type: "success",
					message: "Conta criada com sucesso!",
					description:
						"Acabamos de enviar um link de confirmação para o seu e-mail, verifique as caixas de entrada e de spam",
					duration: 10000,
				});
				navigation.navigate("AuthStack", { screen: "LoginScreen" });
			}
		} catch (e: any) {
			showMessage({ type: "danger", message: "A criação da conta falhou!" });
		} finally {
			setLoading(false);
		}
	};

	return (
		<SafeAreaView style={styles.root}>
			<ScrollView contentContainerStyle={styles.container}>
				<View style={styles.header}>
					<Text style={styles.title}>Criar conta</Text>
					<Text style={styles.subtitle}>
						Bem-vindo(a) ao aplicativo da ISIC. Só precisamos de alguns dados e
						você terá acesso a diversos descontos e conteúdos.
					</Text>
				</View>
				<View style={{ paddingHorizontal: 24 }}>
					<Controller
						control={control}
						rules={{
							required: true,
						}}
						render={({ field: { onChange, onBlur, value } }) => (
							<Dropdown
								label="Tipo da conta:"
								data={[{ value: "Estudante" }, { value: "Responsável" }]}
								onChangeText={onChange}
								value={value}
								baseColor={errors?.accountType ? colors.error : colors.primary}
								labelFontSize={16}
								onBlur={onBlur}
								disabled={loading}
							/>
						)}
						name="accountType"
					/>

					<Controller
						control={control}
						rules={{
							required: true,
						}}
						render={({ field: { onChange, onBlur, value } }) => (
							<InputText
								placeholder="Insira seu nome aqui"
								style={[
									styles.inputs,
									{
										borderColor: errors.name
											? colors.error
											: colors.inputBackgroundColor,
									},
								]}
								onBlur={onBlur}
								onChange={onChange}
								value={value}
								selectionColor={colors.primary}
								textColor={errors.name ? colors.error : colors.text}
								placeholderTextColor={errors.name ? colors.error : colors.text}
								disabled={loading}
							/>
						)}
						name="name"
					/>

					<Controller
						control={control}
						rules={{
							required: true,
						}}
						render={({ field: { onChange, onBlur, value } }) => (
							<InputText
								placeholder="Insira seu e-mail aqui"
								style={[
									styles.inputs,
									{
										borderColor: errors.email
											? colors.error
											: colors.inputBackgroundColor,
									},
								]}
								onBlur={onBlur}
								onChange={onChange}
								value={value}
								selectionColor={colors.primary}
								textColor={errors.email ? colors.error : colors.text}
								placeholderTextColor={errors.email ? colors.error : colors.text}
								disabled={loading}
							/>
						)}
						name="email"
					/>

					<Controller
						control={control}
						rules={{
							required: true,
						}}
						render={({ field: { onChange, onBlur, value } }) => (
							<InputText
								placeholder="Confirme seu e-mail"
								style={[
									styles.inputs,
									{
										borderColor: errors.confirmEmail
											? colors.error
											: colors.inputBackgroundColor,
									},
								]}
								onBlur={onBlur}
								onChange={onChange}
								value={value}
								selectionColor={colors.primary}
								textColor={errors.confirmEmail ? colors.error : colors.text}
								placeholderTextColor={
									errors.confirmEmail ? colors.error : colors.text
								}
								disabled={loading}
							/>
						)}
						name="confirmEmail"
					/>

					<Controller
						control={control}
						rules={{
							required: true,
						}}
						render={({ field: { onChange, onBlur, value } }) => (
							<InputText
								placeholder="Senha"
								secureTextEntry={true}
								style={[
									styles.inputs,
									{
										borderColor: errors.password
											? colors.error
											: colors.inputBackgroundColor,
									},
								]}
								onBlur={onBlur}
								onChange={onChange}
								value={value}
								selectionColor={colors.primary}
								textColor={errors.password ? colors.error : colors.text}
								placeholderTextColor={
									errors.password ? colors.error : colors.text
								}
								disabled={loading}
							/>
						)}
						name="password"
					/>

					<Controller
						control={control}
						rules={{
							required: true,
						}}
						render={({ field: { onChange, onBlur, value } }) => (
							<InputText
								placeholder="Confirme sua senha"
								secureTextEntry={true}
								style={[
									styles.inputs,
									{
										borderColor: errors.confirmPassword
											? colors.error
											: colors.inputBackgroundColor,
									},
								]}
								onBlur={onBlur}
								onChange={onChange}
								value={value}
								selectionColor={colors.primary}
								textColor={errors.confirmPassword ? colors.error : colors.text}
								placeholderTextColor={
									errors.confirmPassword ? colors.error : colors.text
								}
								disabled={loading}
							/>
						)}
						name="confirmPassword"
					/>

					<View style={{ flexDirection: "row", marginTop: 20 }}>
						<Controller
							control={control}
							rules={{
								required: true,
							}}
							render={({ field: { onChange, onBlur, value } }) => (
								<CheckBox
									checkBoxColor={
										errors.termsAgreed ? colors.error : colors.primary
									}
									onClick={() => {
										onChange(!value);
									}}
									isChecked={value}
									leftText={"CheckBox"}
									disabled={loading}
								/>
							)}
							name="termsAgreed"
						/>

						<Text
							style={{
								fontSize: 16,
								marginLeft: 8,
								color: "#999",
							}}
						>
							Ao clicar no botão ao lado, eu concordo com os{" "}
							<TouchableOpacity
								onPress={() => {
									navigation.navigate("TermsOfService");
								}}
								style={{
									height: 18,
									paddingTop: 2,
								}}
							>
								<Text
									style={{
										color: colors.primary,
										fontWeight: "800",
										fontSize: 16,
									}}
								>
									Termos de Uso
								</Text>
							</TouchableOpacity>{" "}
							da ISIC.
						</Text>
					</View>

					<Button
						style={styles.btn}
						labelStyle={styles.btnText}
						mode="contained"
						onPress={handleSubmit(onSubmit)}
						disabled={loading}
					>
						Continuar
					</Button>
					{loading ? <LinearLoading /> : <View style={styles.emptyLoader} />}
				</View>
			</ScrollView>
		</SafeAreaView>
	);
};

export default CreateAccount;

const newStyles = StyleSheet.create({
	nameInput: {},
});

const styles = StyleSheet.create({
	root: {
		flex: 1,
		backgroundColor: "#fff",
		paddingTop: -35,
	},
	container: {
		//alignItems: "center",
	},
	header: {
		paddingHorizontal: 24,
	},
	title: {
		fontWeight: "600",
		fontSize: 32,
		marginBottom: 12,
	},
	subtitle: {
		fontWeight: "200",
		fontSize: 15,
		marginBottom: 16,
	},
	inputs: {
		marginTop: 16,
		width: "100%",
		zIndex: 0,
		borderWidth: 1,
	},
	forgotPassword: {
		alignSelf: "flex-end",
		marginHorizontal: "5%",
	},
	btn: {
		marginTop: 30,
		marginBottom: 10,
		borderRadius: 25,
		width: "100%",
		height: 50,
	},
	btnText: {
		color: "#fff",
		fontSize: 15,
		lineHeight: 30,
	},
	footer: {
		marginTop: 80,
	},
	text: {
		textAlign: "center",
		color: "#999999",
	},
	linkText: { fontWeight: "700", textDecorationLine: "underline" },
	emptyLoader: {
		height: 4,
	},
});
